import type { CSSProperties } from 'react';

import { cn } from '../../utils/cn';
import type { BevelInsetWrapperProps } from './BevelInsetWrapper.types';
import { buttonInsetWrapperVariants } from './Button.variants';

/**
 * Inset bevel-highlight wrapper — web variant. Plain absolutely-positioned
 * `<div>` that pins the `BevelHighlight` (CSS inset box-shadow) inside
 * the button's rounded shape. The per-size class supplies the SSR
 * fallback radius; once hydrated the wrapper inherits the button's OWN
 * corners, so a consumer's `rounded-full` (or any outer override) is traced
 * too — a fixed px radius inside a pill left the highlight's top edge
 * running straight into the curve, reading as a clipped extra stroke.
 *
 * `animationClassName` from the web hook is typically
 * `transition-opacity duration-150 ease-out group-active:opacity-0`, so
 * the bevel fades smoothly on press. `webAnimationStyle` adds inline
 * `transitionDuration` / `transitionTimingFunction` overrides when the
 * caller customises animation timing on the Button.
 *
 * Defensive guard: this file should only render on web because Metro is
 * supposed to resolve to the `.native.tsx` sibling. The `typeof document`
 * check returns null on native if a stale Metro cache loads this by
 * mistake. Restart Metro with `--clear` after creating any new
 * `.native.tsx` sibling.
 */
export function BevelInsetWrapper({
  size,
  // Native-only: the SVG sibling strokes to it; here the corners are inherited.
  radius: _radius,
  radii,
  animationClassName,
  webAnimationStyle,
  className,
  children,
}: BevelInsetWrapperProps) {
  if (typeof document === 'undefined') return null;

  const style: CSSProperties = {
    ...(radii ?? { borderRadius: 'inherit' }),
    ...(webAnimationStyle ?? null),
  };

  return (
    <div
      aria-hidden
      className={cn(buttonInsetWrapperVariants({ size }), animationClassName, className)}
      style={style}>
      {children}
    </div>
  );
}
